import React, { useState, useEffect } from 'react';
import { AlertTriangle, Phone, Clock, MapPin, Video, MessageSquare, CheckCircle, XCircle } from 'lucide-react';

interface EmergencyAlert {
  id: string;
  timestamp: Date;
  type: 'FALL' | 'MEDICAL' | 'BEHAVIORAL';
  status: 'ACTIVE' | 'RESPONDING' | 'RESOLVED';
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  location: string;
  description: string;
  vitalSigns?: {
    heartRate?: number;
    bloodPressure?: string;
    temperature?: number;
  };
  responseTeam?: {
    name: string;
    eta?: number;
    status: 'DISPATCHED' | 'EN_ROUTE' | 'ON_SCENE';
  };
}

interface Props {
  alerts: EmergencyAlert[];
}

const EmergencyAlerts: React.FC<Props> = ({ alerts }) => {
  const [selectedAlert, setSelectedAlert] = useState<EmergencyAlert | null>(null);
  const [streamActive, setStreamActive] = useState(false);

  useEffect(() => {
    // Auto-select the most recent high-priority active alert
    const highPriorityAlert = alerts.find(
      alert => alert.priority === 'HIGH' && alert.status === 'ACTIVE'
    );
    if (highPriorityAlert) {
      setSelectedAlert(highPriorityAlert);
      setStreamActive(true);
    }
  }, [alerts]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'text-red-500';
      case 'RESPONDING':
        return 'text-yellow-500';
      case 'RESOLVED':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimestamp = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    }).format(date);
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alert List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-xl font-bold mb-4">Active Alerts</h2>
            <div className="space-y-4">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedAlert?.id === alert.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setSelectedAlert(alert);
                    setStreamActive(true);
                  }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center">
                      <AlertTriangle className={getStatusColor(alert.status)} size={20} />
                      <span className="ml-2 font-medium">{alert.type}</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(alert.priority)}`}>
                      {alert.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{alert.description}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock size={14} className="mr-1" />
                    <span>{formatTimestamp(alert.timestamp)}</span>
                    <MapPin size={14} className="ml-3 mr-1" />
                    <span>{alert.location}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Live Stream and Response */}
        <div className="lg:col-span-2">
          {selectedAlert ? (
            <div className="space-y-6">
              {/* Live Stream */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Live Stream - {selectedAlert.location}</h3>
                    <div className="flex items-center">
                      {streamActive ? (
                        <>
                          <span className="flex h-3 w-3 mr-2">
                            <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                          </span>
                          <span className="text-sm text-red-500 font-medium">LIVE</span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">Stream Inactive</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="aspect-video bg-gray-900 relative">
                  {streamActive ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-white text-center">
                        <Video className="mx-auto mb-2" size={48} />
                        <p>Live stream would be displayed here</p>
                        <p className="text-sm text-gray-400">Showing: {selectedAlert.location}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                        onClick={() => setStreamActive(true)}
                      >
                        Start Stream
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Response Actions */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Response Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button className="flex items-center justify-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                    <Phone className="mr-2" size={18} />
                    Contact Emergency Services
                  </button>
                  <button className="flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                    <MessageSquare className="mr-2" size={18} />
                    Contact Family Members
                  </button>
                </div>
              </div>

              {/* Alert Details */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Alert Details</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className={`font-medium ${getStatusColor(selectedAlert.status)}`}>
                        {selectedAlert.status}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Priority</p>
                      <p className="font-medium">{selectedAlert.priority}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-medium">{selectedAlert.location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Time</p>
                      <p className="font-medium">{formatTimestamp(selectedAlert.timestamp)}</p>
                    </div>
                  </div>
                  {selectedAlert.vitalSigns && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Vital Signs</p>
                      <div className="grid grid-cols-3 gap-4">
                        {selectedAlert.vitalSigns.heartRate && (
                          <div>
                            <p className="text-xs text-gray-500">Heart Rate</p>
                            <p className="font-medium">{selectedAlert.vitalSigns.heartRate} BPM</p>
                          </div>
                        )}
                        {selectedAlert.vitalSigns.bloodPressure && (
                          <div>
                            <p className="text-xs text-gray-500">Blood Pressure</p>
                            <p className="font-medium">{selectedAlert.vitalSigns.bloodPressure}</p>
                          </div>
                        )}
                        {selectedAlert.vitalSigns.temperature && (
                          <div>
                            <p className="text-xs text-gray-500">Temperature</p>
                            <p className="font-medium">{selectedAlert.vitalSigns.temperature}°F</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedAlert.responseTeam && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Response Team</p>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{selectedAlert.responseTeam.name}</p>
                            <p className="text-sm text-gray-500">Status: {selectedAlert.responseTeam.status}</p>
                          </div>
                          {selectedAlert.responseTeam.eta && (
                            <div className="text-right">
                              <p className="text-sm text-gray-500">ETA</p>
                              <p className="font-medium">{selectedAlert.responseTeam.eta} min</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Response Log */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Response Log</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1" size={18} />
                    <div>
                      <p className="text-sm font-medium">Alert Generated</p>
                      <p className="text-xs text-gray-500">{formatTimestamp(selectedAlert.timestamp)}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1" size={18} />
                    <div>
                      <p className="text-sm font-medium">Fall Detected - High Priority</p>
                      <p className="text-xs text-gray-500">AI Analysis Confirmed</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-yellow-500 mt-1" size={18} />
                    <div>
                      <p className="text-sm font-medium">Emergency Services Notified</p>
                      <p className="text-xs text-gray-500">Dispatch Confirmed</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <XCircle className="text-gray-400 mt-1" size={18} />
                    <div>
                      <p className="text-sm font-medium">Response Team Arrival</p>
                      <p className="text-xs text-gray-500">Pending</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <AlertTriangle className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-xl font-semibold mb-2">No Alert Selected</h3>
              <p className="text-gray-500">Select an alert from the list to view details and live stream</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmergencyAlerts; 